/* eslint-disable @typescript-eslint/no-extra-semi */
import { Component } from 'react';
import { Alert, ControlLabel, FormControl, FormGroup, Navbar } from 'react-bootstrap';
import { connect } from 'react-redux';
import { IndexLink } from 'react-router';
import { createStructuredSelector } from 'reselect';
import * as _ from 'lodash';
import type { UpdateProjectGroupRequest } from '../../../../typings/api';
import { Action } from '../../../../typings/permissions';
import { window } from '../../../globals';
import {
  userHasPermissionForReleaseGroup,
  userHasPermissionToSetReleaseGroupPolicy,
} from '../../../helpers/permissions';
import OrganizationsModule from '../../../redux/organizations';
import ProjectGroupModule from '../../../redux/project_grouping';
import type { IAsyncStatus, Policy, ProjectGroup, SelectOption } from '../../../types';
import { PolicyType } from '../../../types';
import Button from '../../generic/Button';
import Icon, { icons, IconText } from '../../generic/Icon';
import PolicySettings from './components/PolicySettings';
import ProjectGroupTokenSettings from './components/ProjectGroupPortalSettings';
import { shouldDisplayOrganizationPortalTokenSettings } from '../../settings/shared/permissions';
import { commitTokenChanges } from './request';

type ProjectGroupSettingsState = {
  projectGroupTitle: string;
  changesMade: boolean;
  selectedLicensingPolicy: Policy | null;
  selectedSecurityPolicy: Policy | null;
  selectedQualityPolicy: Policy | null;
  showUpdate: boolean;
  pendingAddTokenIds: number[];
  pendingRemoveTokenIds: number[];
  publicOnPortal: boolean | null;
  tokenError: string | null;
};

type ReduxProps = {
  licensingPolicies: Policy[];
  securityPolicies: Policy[];
  qualityPolicies: Policy[];
  projectGroupStatus: IAsyncStatus<Error>;
  organizationStatus: IAsyncStatus;
  fetchOrganization(id: number): Promise<void>;
  updateSettings(projectGroupId: number, requestData: UpdateProjectGroupRequest): Promise<void>;
};

type ProjectGroupSettingsProps = {
  projectGroup: ProjectGroup;
} & ReduxProps;

/**
 * @deprecated - Use the `revamp/settings/` instead. This will be removed after `releaseGroupsRevamp` is GA
 */
class ProjectGroupSettings extends Component<ProjectGroupSettingsProps, ProjectGroupSettingsState> {
  readonly state = {
    projectGroupTitle: this.props.projectGroup.title,
    changesMade: false,
    selectedLicensingPolicy: null,
    selectedSecurityPolicy: null,
    selectedQualityPolicy: null,
    showUpdate: false,
    pendingAddTokenIds: [],
    pendingRemoveTokenIds: [],
    publicOnPortal: this.props.projectGroup.publicOnPortal,
    tokenError: null,
  };

  async componentDidMount() {
    if (window.user) {
      await this.props.fetchOrganization(window.user.organizationId as number);
    }
  }

  async componentDidUpdate() {
    const { projectGroup, licensingPolicies, securityPolicies, qualityPolicies } = this.props;
    const { selectedLicensingPolicy, selectedSecurityPolicy, selectedQualityPolicy } = this.state;
    // Once the policy lists have loaded,
    // if the project group has any designated policies that aren't selected yet,
    // select them in the dropdowns
    if ((!_.isEmpty(licensingPolicies) && projectGroup.policyId && !selectedLicensingPolicy)
      || (!_.isEmpty(securityPolicies) && projectGroup.securityPolicyId && !selectedSecurityPolicy)
      || (!_.isEmpty(qualityPolicies) && projectGroup.qualityPolicyId && !selectedQualityPolicy)) {
      this.selectProjectGroupPolicies();
    }
  };

  selectProjectGroupPolicies = () => {
    const {
      licensingPolicies,
      securityPolicies,
      qualityPolicies,
      projectGroup,
    } = this.props;
    const { policyId, securityPolicyId, qualityPolicyId } = projectGroup;
    const selectedLicensingPolicy = licensingPolicies.find(policy => policy.id === policyId) ?? null;
    const selectedSecurityPolicy = securityPolicies.find(policy => policy.id === securityPolicyId) ?? null;
    const selectedQualityPolicy = qualityPolicies.find(policy => policy.id === qualityPolicyId) ?? null;
    this.setState({
      selectedLicensingPolicy,
      selectedSecurityPolicy,
      selectedQualityPolicy,
    });
  };

  onChangeProjectGroupTitle = (event) => {
    this.setState({
      projectGroupTitle: event.target.value,
      changesMade: true,
    });
  };

  onChangeDropdown = (option: { label: string, value: string }, settingName: string) => {
    this.setState({
      [settingName]: option,
      changesMade: true,
    });
  };

  onChangeLicensingPolicy = (opt: SelectOption) => this.onChangeDropdown(opt, 'selectedLicensingPolicy');

  onChangeSecurityPolicy = (opt: SelectOption) => this.onChangeDropdown(opt, 'selectedSecurityPolicy');

  onChangeQualityPolicy = (opt: SelectOption) => this.onChangeDropdown(opt, 'selectedQualityPolicy');

  onDismissAlert = () => this.setState({ showUpdate: false });

  onAddToken = (tokenId: number) => {
    this.setState(previousState => ({
      pendingAddTokenIds: _.uniq([...previousState.pendingAddTokenIds, tokenId]),
      changesMade: true,
    }));
  };

  onRemoveToken = (tokenId: number) => {
    this.setState(previousState => ({
      // Remove the uncommitted token from the list of pending adds
      pendingAddTokenIds: previousState.pendingAddTokenIds.filter(id => id !== tokenId),
      pendingRemoveTokenIds: _.uniq([...previousState.pendingRemoveTokenIds, tokenId]),
      changesMade: true,
    }));
  };

  onCommitChanges = async () => {
    try {
      await commitTokenChanges(
        this.props.projectGroup.id,
        this.state.pendingAddTokenIds,
        this.state.pendingRemoveTokenIds,
      );
    } catch (error) {
      this.setState({
        tokenError: 'An error occurred while updating the portal tokens.',
      });
      return;
    }
    await this.props.updateSettings(this.props.projectGroup.id, {
      title: this.state.projectGroupTitle,
      licensingPolicyId: (this.state.selectedLicensingPolicy && this.state.selectedLicensingPolicy.id) || undefined,
      securityPolicyId: (this.state.selectedSecurityPolicy && this.state.selectedSecurityPolicy.id) || undefined,
      qualityPolicyId: (this.state.selectedQualityPolicy && this.state.selectedQualityPolicy.id) || undefined,
      publicOnPortal: this.state.publicOnPortal,
    });
    this.setState({
      changesMade: false,
      showUpdate: true,
      pendingAddTokenIds: [],
      pendingRemoveTokenIds: [],
      tokenError: null,
    });
  };

  renderSettingsUpdateStatus = () => {
    let alertMsg = 'Settings updated.';
    let alertStyle = 'success';
    if (this.props.projectGroupStatus.error) {
      alertMsg = this.props.projectGroupStatus.error.message || 'An error occurred.';
      alertStyle = 'error';
    }
    if (!_.isEmpty(this.props.projectGroupStatus.errors)) {
      alertMsg = (this.props.projectGroupStatus.errors || []).map(error => error.message).join(',');
      alertStyle = 'error';
    }
    if (this.state.tokenError) {
      alertMsg = this.state.tokenError;
      alertStyle = 'error';
    }
    return (
      <Alert className="text-center" dismissAfter={2000} onDismiss={this.onDismissAlert} bsStyle={alertStyle} style={{ marginTop: '-20px' }}>
        {alertMsg}
      </Alert>
    );
  };

  render() {
    const canEditGroup = userHasPermissionForReleaseGroup(Action.Edit, this.props.projectGroup.id);
    const showSaveButton = canEditGroup ||
      [PolicyType.LICENSING, PolicyType.SECURITY, PolicyType.QUALITY].some(
        (policyType: PolicyType) => userHasPermissionToSetReleaseGroupPolicy(this.props.projectGroup.id, policyType),
      );

    return (
      <div>
        <Navbar className="subnav">
          <ul className="nav nav-pills">
            <li>
              <IndexLink activeClassName="active" to={`/projects/group/${this.props.params.projectGroupId}/settings`}>
                <Icon icon={icons.customize} />&nbsp;General
              </IndexLink>
            </li>
            {showSaveButton && (
              <li className="pull-right" style={{ marginTop: '2px' }}>
                <Button
                  type="primary"
                  size="medium"
                  onClick={this.onCommitChanges}
                  isDisabled={!this.state.changesMade || this.props.projectGroupStatus.loading}
                >
                  <IconText
                    icon={this.props.projectGroupStatus.loading ? icons.circleO : icons.save}
                    hasSpin={this.props.projectGroupStatus.loading}
                    text="Save Changes"
                  />
                </Button>
              </li>
            )}
          </ul>
        </Navbar>
        <div className="container edit-project-group-release">
          {
            (this.state.showUpdate
              || this.props.projectGroupStatus.error
              || !_.isEmpty(this.props.projectGroupStatus.errors)
              || this.state.tokenError) &&
            this.renderSettingsUpdateStatus()
          }
          <div className="panel card">
            <FormGroup>
              <ControlLabel>Release Group Title</ControlLabel>
              <FormControl
                type="text"
                name="username"
                value={this.state.projectGroupTitle}
                disabled={!canEditGroup}
                placeholder="Your release group title"
                onChange={this.onChangeProjectGroupTitle}
              />
            </FormGroup>
            <br />
            <PolicySettings
              settingsMode={{ mode: 'editing', releaseGroupId: this.props.projectGroup.id }}
              onChangeLicensingPolicy={this.onChangeLicensingPolicy}
              onChangeSecurityPolicy={this.onChangeSecurityPolicy}
              onChangeQualityPolicy={this.onChangeQualityPolicy}
              selectedSecurityPolicy={this.state.selectedSecurityPolicy}
              selectedLicensingPolicy={this.state.selectedLicensingPolicy}
              selectedQualityPolicy={this.state.selectedQualityPolicy}
              licensingPolicies={this.props.licensingPolicies}
              securityPolicies={this.props.securityPolicies}
              qualityPolicies={this.props.qualityPolicies}
              isLoadingPolicyOptions={this.props.organizationStatus.loading}
            />
            {shouldDisplayOrganizationPortalTokenSettings() && (
              <>
                <br />
                <ProjectGroupTokenSettings
                  projectGroupId={this.props.projectGroup.id}
                  onAddToken={this.onAddToken}
                  onRemoveToken={this.onRemoveToken}
                  publicOnPortal={this.state.publicOnPortal}
                  setPublicOnPortal={(publicOnPortal) => this.setState({ publicOnPortal, changesMade: true })}
                />
              </>
            )}
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = createStructuredSelector({
  licensingPolicies: OrganizationsModule.selectors.getCurrentOrganizationLicensingPolicies,
  securityPolicies: OrganizationsModule.selectors.getCurrentOrganizationSecurityPolicies,
  qualityPolicies: OrganizationsModule.selectors.getCurrentOrganizationQualityPolicies,
  projectGroupStatus: ProjectGroupModule.selectors.getStatus,
  organizationStatus: OrganizationsModule.selectors.getStatus,
});

const mapDispatchToProps = {
  fetchOrganization: OrganizationsModule.actions.fetchOne,
  updateSettings: ProjectGroupModule.actions.update,
};

export default connect(mapStateToProps, mapDispatchToProps)(ProjectGroupSettings);
